import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { joinList, formatReach, formatMoney } from "../inventory/parse";
import { formatDateTime } from "../inventory/time";
import { lineShare, formatAmount, formatCount } from "./planMath";

// A plan as a client-ready sheet: details at the top, one row per channel,
// totals at the bottom. Numbers stay numbers so the client can work with them.
// Money is never added across currencies: budgets and totals come one row per
// currency, and each line's share is of its own currency's spend.
export function exportPlanXlsx(plan, lines, totals) {
  const period = [plan.startDate, plan.endDate].filter(Boolean).join(" – ");
  const head = [
    ["Media plan", plan.name],
    ["Client", plan.client || ""],
    ["Period", period],
    ["Status", plan.status],
    ...totals.byCurrency
      .filter((c) => c.planned > 0)
      .map((c) => [`Budget (${c.currency})`, c.planned, c.currency]),
    ["Prepared", formatDateTime(Date.now())],
    [],
  ];

  const columns = [
    "Channel",
    "Partner",
    "Territory",
    "Format",
    "Reach",
    "List CPM",
    "Plan CPM",
    "Currency",
    "Budget",
    "Share of budget",
    "Est. impressions",
  ];

  const rows = lines.map((l) => [
    l.channel.channelName,
    l.channel.partner,
    joinList(l.channel.territories),
    joinList(l.channel.formats),
    formatReach(l.channel.reach),
    l.listCpm ?? "",
    l.cpm ?? "",
    l.currency,
    l.budget,
    lineShare(l, totals) ?? "",
    l.impressions ?? "",
  ]);

  const foot = [
    [],
    ...totals.byCurrency
      .filter((c) => c.channels > 0)
      .map((c) => [`Total ${c.currency}`, "", "", "", "", "", c.avgCpm ?? "", c.currency, c.allocated, "", c.impressions]),
    ["Total impressions", "", "", "", "", "", "", "", "", "", totals.impressions],
  ];
  if (plan.notes) foot.push([], ["Notes", plan.notes]);

  const ws = XLSX.utils.aoa_to_sheet([...head, columns, ...rows, ...foot]);
  ws["!cols"] = [28, 16, 14, 14, 9, 10, 10, 9, 12, 14, 16].map((wch) => ({ wch }));

  // Number formats for the money, share and count columns
  const first = head.length + 1;
  for (let r = first; r < first + rows.length + foot.length; r += 1) {
    [5, 6, 8].forEach((c) => {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell && typeof cell.v === "number") cell.z = "#,##0.00";
    });
    const share = ws[XLSX.utils.encode_cell({ r, c: 9 })];
    if (share && typeof share.v === "number") share.z = "0.0%";
    const imp = ws[XLSX.utils.encode_cell({ r, c: 10 })];
    if (imp && typeof imp.v === "number") imp.z = "#,##0";
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Plan");
  XLSX.writeFile(wb, `${fileName(plan)}.xlsx`);
}

const fileName = (plan) => plan.name.replace(/[\\/:*?"<>|]+/g, "").trim() || "media-plan";

// jsPDF's built-in fonts only cover Latin-1, so channel names with ş, ğ or İ
// (and the € sign) would come out garbled. Roboto is loaded from /fonts the
// first time a PDF is made.
let fontData = null;

async function loadFonts() {
  if (fontData) return fontData;
  const toBase64 = (buf) => {
    const bytes = new Uint8Array(buf);
    let bin = "";
    for (let i = 0; i < bytes.length; i += 0x8000) {
      bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    }
    return btoa(bin);
  };
  const [regular, bold] = await Promise.all(
    ["Roboto-Regular", "Roboto-Bold"].map((f) =>
      fetch(`/fonts/${f}.ttf`).then((r) => {
        if (!r.ok) throw new Error(`Could not load ${f}`);
        return r.arrayBuffer();
      })
    )
  );
  fontData = { regular: toBase64(regular), bold: toBase64(bold) };
  return fontData;
}

// The same plan as the sheet, as a PDF to send: details at the top, the
// channel table, totals per currency at the bottom.
export async function exportPlanPdf(plan, lines, totals) {
  const fonts = await loadFonts();
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  doc.addFileToVFS("Roboto-Regular.ttf", fonts.regular);
  doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
  doc.addFileToVFS("Roboto-Bold.ttf", fonts.bold);
  doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");

  const margin = 40;
  doc.setFont("Roboto", "bold");
  doc.setFontSize(18);
  doc.text(plan.name, margin, 50);

  const period = [plan.startDate, plan.endDate].filter(Boolean).join(" – ");
  const details = [
    ["Client", plan.client || "—"],
    ["Period", period || "—"],
    ["Status", plan.status],
    ...totals.byCurrency
      .filter((c) => c.planned > 0)
      .map((c) => [`Total budget (${c.currency})`, formatAmount(c.planned, c.currency)]),
    ["Prepared", formatDateTime(Date.now())],
  ];
  autoTable(doc, {
    startY: 62,
    body: details,
    theme: "plain",
    styles: { font: "Roboto", fontSize: 10, cellPadding: 2 },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 130 } },
    margin: { left: margin, right: margin },
  });

  // Footer cells don't take columnStyles, so the numbers are right-aligned
  // here to sit under the column's own figures
  const right = (content) => ({ content, styles: { halign: "right" } });
  const foot = totals.byCurrency
    .filter((c) => c.channels > 0)
    .map((c) => ["Total", "", "", "", "", "", "", right(formatAmount(c.allocated, c.currency)), "", right(formatCount(c.impressions))]);
  // With one currency its row already has the impressions total
  if (foot.length > 1) {
    foot.push(["Total impressions", "", "", "", "", "", "", "", "", right(formatCount(totals.impressions))]);
  }

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 16,
    head: [["Channel", "Partner", "Territory", "Format", "Reach", "List CPM", "Plan CPM", "Budget", "Share of budget", "Est. impressions"]],
    body: lines.map((l) => {
      const share = lineShare(l, totals);
      return [
        l.channel.channelName,
        l.channel.partner,
        joinList(l.channel.territories) || "—",
        joinList(l.channel.formats) || "—",
        formatReach(l.channel.reach) || "—",
        l.listCpm == null ? "—" : formatMoney(l.listCpm, l.currency),
        l.cpm == null ? "—" : formatMoney(l.cpm, l.currency),
        formatAmount(l.budget, l.currency),
        share == null ? "—" : `${(share * 100).toFixed(1)}%`,
        formatCount(l.impressions),
      ];
    }),
    foot,
    showFoot: "lastPage",
    styles: { font: "Roboto", fontSize: 9, cellPadding: 5 },
    headStyles: { fillColor: [66, 133, 244], fontStyle: "bold" },
    footStyles: { fillColor: [241, 243, 244], textColor: 20, fontStyle: "bold" },
    columnStyles: Object.fromEntries([4, 5, 6, 7, 8, 9].map((i) => [i, { halign: "right" }])),
    margin: { left: margin, right: margin },
  });

  if (plan.notes) {
    const y = doc.lastAutoTable.finalY + 24;
    doc.setFont("Roboto", "bold");
    doc.setFontSize(10);
    doc.text("Notes", margin, y);
    doc.setFont("Roboto", "normal");
    doc.text(doc.splitTextToSize(plan.notes, doc.internal.pageSize.getWidth() - margin * 2), margin, y + 14);
  }

  doc.save(`${fileName(plan)}.pdf`);
}
